import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/apiClient";
import { BookOpen, FileText, MapPin, CalendarCheck, Download } from "lucide-react";

const fileUrl = (path) => `${process.env.REACT_APP_BACKEND_URL}${path}`;

/* Página pública do estudo prévio: ouvir o áudio e abrir os PDFs, sem login. */
export default function PublicStudy({ source = "study" }) {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(source === "communique" ? `/public/communique/${token}` : `/public/study/${token}`).then((r) => setData(r.data)).catch((e) => setError(formatApiError(e)));
  }, [token, source]);

  const audios = data?.files.filter((f) => f.kind === "audio") || [];
  const docs = data?.files.filter((f) => f.kind !== "audio") || [];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex justify-center p-6">
      <div className="max-w-2xl w-full pt-10 kw-fade-up" data-testid="study-page">
        <div className="kw-overline text-[#E50000] mb-2 flex items-center gap-2"><BookOpen className="w-3.5 h-3.5" /> {source === "communique" ? "Comunicado" : "Estudo prévio"}</div>
        {error && <p className="text-[#E50000]" data-testid="study-error">{error}</p>}
        {data && (
          <>
            <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tight" data-testid="study-title">{data.title}</h1>
            {data.when_label && <div className="text-sm text-[#A3A3A3] mt-3 flex items-center gap-1.5"><CalendarCheck className="w-4 h-4" /> {data.when_label}</div>}
            {data.location && <div className="text-xs text-[#737373] mt-1 flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {data.location}</div>}
            {data.message
              ? <p className="text-sm text-[#A3A3A3] mt-6 whitespace-pre-line" data-testid="study-message">{data.message}</p>
              : <p className="text-sm text-[#A3A3A3] mt-6">Olá {(data.name || "").split(" ")[0]}, prepara-te para a sessão com este material.</p>}

            {audios.map((f) => (
              <div key={f.token} className="kw-card p-5 mt-6" data-testid={`study-audio-${f.token}`}>
                <div className="kw-overline mb-3">Ouvir · {f.filename}</div>
                <audio controls preload="metadata" src={fileUrl(f.url)} className="w-full" />
              </div>
            ))}

            {docs.length > 0 && (
              <div className="mt-8 space-y-2">
                <div className="kw-overline">Documentos</div>
                {docs.map((f) => (
                  <a key={f.token} href={fileUrl(f.url)} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 kw-card px-4 py-3 hover:border-[#E50000] transition-colors"
                    data-testid={`study-doc-${f.token}`}>
                    <FileText className="w-4 h-4 text-[#E50000]" />
                    <span className="flex-1 truncate text-sm">{f.filename}</span>
                    <Download className="w-4 h-4 text-[#737373]" />
                  </a>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
